import React from 'react';
import { H1 } from '../../components/elements/Text';
import Navbar from '../../components/Navbar';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import PageWrapper from '../../components/PageWrapper';
import styled from 'styled-components';
import { useGetCompanies } from '../../hooks/use-hydra/admin';
import { createColumnHelper } from '@tanstack/react-table';
import { CompaniesResponse } from '../../api/admin';

import { useHistory } from 'react-router-dom';
import day from 'dayjs';
import utc from 'dayjs/plugin/utc';
import AddCompanyModal from '../../components/Admin/Modals/AddCompanyModal';
import { ROUTES } from '../../routing';
import Table from '../../components/Table/CompactTable';
import {
  CollapsibleSectionFooterButton,
  CollapsibleSectionLabel,
} from '../../components/Admin/CollapsibleSection';
import { EmptyDiv } from '../../components/AssessmentTest/common';
day.extend(utc);

const TableWrapper = styled.div`
  .table-scroll-wrapper {
    padding-bottom: 0;
  }
  .companies-table {
    tbody > tr:last-child > td {
      border-bottom: none;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
`;
type Company = CompaniesResponse['items'][number];

const Header = () => (
  <Navbar
    breadCrumbs={[
      {
        label: 'Home',
        url: ROUTES.HOME.getPath(),
      },
      {
        label: 'Teams',
        url: null,
      },
    ]}
  />
);

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  margin-bottom: 72px;
  gap: 32px;
  overflow-y: auto;
`;

export default function Companies() {
  const { data, isLoading, error, refetch } = useGetCompanies();
  const history = useHistory();
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] =
    React.useState(false);

  const onGoToCompany = (id: string) => {
    history.push(ROUTES.ADMIN.COMPANY.getPath({ companyId: id }));
  };

  return (
    <PageWithAsyncData isLoading={isLoading} error={error} retry={refetch}>
      <Header />
      <PageWrapper>
        <H1>Companies</H1>
        <Content>
          <TableWrapper>
            <Table
              data={data?.items ?? []}
              getRowId={(row) => row.id.toString()}
              columns={columns}
              className="companies-table"
              footer={
                <TableFooter
                  companies={data?.items ?? []}
                  onAddCompany={() => setIsAddCompanyModalOpen(true)}
                />
              }
              getRowProps={(row) => ({
                onClick: () => onGoToCompany(row.original.id.toString()),
              })}
            />
          </TableWrapper>
        </Content>
        <EmptyDiv />
      </PageWrapper>
      <AddCompanyModal
        isOpen={isAddCompanyModalOpen}
        onClose={() => setIsAddCompanyModalOpen(false)}
      />
    </PageWithAsyncData>
  );
}

const columnHelper = createColumnHelper<Company>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
  }),
  columnHelper.display({
    id: 'latestCohort',
    header: 'Latest Learning Cohort',
    cell: ({ row }) => {
      return `${
        row.original.latestCohortStartDate && row.original.latestCohortEndDate
          ? day.utc(row.original.latestCohortStartDate).format('DD/MM/YYYY') +
            ' - ' +
            day.utc(row.original.latestCohortEndDate).format('DD/MM/YYYY')
          : '-'
      }`;
    },
  }),
  columnHelper.accessor('numLearners', {
    header: 'Num of Learners',
  }),
];

export const FooterWrapper = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  gap: 16px;
  overflow-x: auto;
  padding: 10px 16px;
  text-align: right;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const TableFooter = ({
  companies,
  onAddCompany,
}: {
  companies: Company[];
  onAddCompany: () => void;
}) => {
  return (
    <FooterWrapper>
      <CollapsibleSectionLabel>
        {companies.length} Companies
      </CollapsibleSectionLabel>
      <Row>
        <CollapsibleSectionFooterButton
          label="Add Company"
          faIcon="fa-solid fa-plus"
          showIconBeforeLabel
          onClick={() => {
            onAddCompany();
          }}
        />
      </Row>
    </FooterWrapper>
  );
};
